/**
 * Incremental parser for the Server-Sent Events wire format. Feed it text as it arrives; it handles
 * chunks that split a line anywhere, CRLF/LF/CR line endings, multi-line `data`, comment lines (the
 * server's `: ready` and `: hb`) and the `retry:` field.
 */

export interface SseMessage {
  /** The `event:` name; `message` when absent. */
  event: string;
  data: string;
  id?: string;
}

export interface SseHandlers {
  onMessage: (message: SseMessage) => void;
  /** A comment line, without the leading colon and surrounding whitespace. */
  onComment?: (text: string) => void;
  /** The server's advertised reconnection delay in milliseconds. */
  onRetry?: (milliseconds: number) => void;
}

export interface SseParser {
  push(chunk: string): void;
}

export function createSseParser(handlers: SseHandlers): SseParser {
  let buffer = '';
  let skipLeadingLf = false;
  let event = '';
  let id: string | undefined;
  let dataLines: string[] = [];

  function dispatch(): void {
    if (dataLines.length > 0) {
      handlers.onMessage({ event: event || 'message', data: dataLines.join('\n'), id });
    }
    event = '';
    id = undefined;
    dataLines = [];
  }

  function line(text: string): void {
    if (text === '') return dispatch();
    if (text.startsWith(':')) return handlers.onComment?.(text.slice(1).trim());

    const colon = text.indexOf(':');
    const field = colon === -1 ? text : text.slice(0, colon);
    let value = colon === -1 ? '' : text.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    switch (field) {
      case 'event':
        event = value;
        break;
      case 'data':
        dataLines.push(value);
        break;
      case 'id':
        id = value;
        break;
      case 'retry':
        if (/^\d+$/.test(value)) handlers.onRetry?.(Number(value));
        break;
      default:
        break;
    }
  }

  return {
    push(chunk) {
      let text = chunk;
      if (skipLeadingLf && text.startsWith('\n')) text = text.slice(1);
      skipLeadingLf = false;
      buffer += text;

      let start = 0;
      for (let i = 0; i < buffer.length; i += 1) {
        const char = buffer[i];
        if (char !== '\n' && char !== '\r') continue;
        line(buffer.slice(start, i));
        if (char === '\r') {
          if (buffer[i + 1] === '\n') i += 1;
          else if (i + 1 === buffer.length) skipLeadingLf = true;
        }
        start = i + 1;
      }
      buffer = buffer.slice(start);
    },
  };
}
